import { useCallback, useMemo } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { groomingKeys, useGroomingBacklog } from "@/hooks/queries/use-grooming";
import type { GroomingAiSuggestion } from "@/lib/api/grooming";

/**
 * Each item's latest brief as the backlog read last saw it (epic
 * PI-SPEC-SW-E289, UX-PM-09): its id, comment status and whether it is out
 * of date.
 *
 * Batch reads carry no staleness, and a `current` batch item carries no
 * brief, so the run list and the Agenda take both from the project's
 * whole-backlog read, `useGroomingBacklog(project, "all", "rank")`. This hook
 * only reads that query from the cache (`cacheOnly`): Session build and
 * Present make the read; the run page never starts it, so it adds no ADO
 * read. It updates when the cached read does.
 *
 * The returned function gives the row's `aiSuggestion`, or `null` when it is
 * unknown (no cached backlog, the item has no AI brief, or it is not on the
 * backlog). Compare `briefId` before applying `stale` to another brief
 * (`batchItemBriefState`).
 */
export function useBacklogBriefs(
  project: string | undefined
): (itemId: string) => Pick<GroomingAiSuggestion, "briefId" | "commentStatus" | "stale"> | null {
  const backlog = useGroomingBacklog(project, "all", "rank", { cacheOnly: true });
  const data = project ? backlog.data : undefined;
  const byId = useMemo(() => {
    const map = new Map<string, GroomingAiSuggestion>();
    if (!data) return map;
    for (const row of [...data.items, ...data.exceptions]) {
      if (row.aiSuggestion) map.set(row.itemId, row.aiSuggestion);
    }
    return map;
  }, [data]);
  return useCallback((itemId: string) => byId.get(itemId) ?? null, [byId]);
}

/**
 * Mark the project's cached backlog reads out of date after a brief finished
 * or changed, so the next page that shows the backlog reads it again. Only a
 * page that already reads the backlog itself (the Backlog page, Session
 * build, Present) refetches now (`refetchType: "active"`); a cache-only
 * reader such as {@link useBacklogBriefs} starts no ADO read.
 */
export function refreshCachedBacklog(queryClient: QueryClient, project: string): void {
  void queryClient.invalidateQueries({ queryKey: [...groomingKeys.project(project), "backlog"], refetchType: "active" });
}
